import axios from "axios";
import packageJson from "../../package.json";

const CLIENT_CHANNEL = "web";
const CLIENT_VERSION = packageJson.version || "unknown";
function getClientTimezone() {
  const tz = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone;
  return tz || "UTC";
}

const CLIENT_TIMEZONE = getClientTimezone();
const CLIENT_HEADERS = {
  "X-Client-Channel": CLIENT_CHANNEL,
  "X-Client-Version": CLIENT_VERSION,
  "X-Timezone": CLIENT_TIMEZONE,
};

function applyClientHeaders(headers = {}) {
  return {
    ...CLIENT_HEADERS,
    ...headers,
  };
}

axios.defaults.headers.common = applyClientHeaders(axios.defaults.headers.common);

let refreshTokenRequest = null;

function base64UrlDecode(input) {
  try {
    const base64 = input.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4 || 4)) % 4, "=");
    return decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
  } catch (e) {
    return null;
  }
}

export function parseJwt(token) {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  const payload = base64UrlDecode(parts[1]);
  if (!payload) return null;
  try {
    return JSON.parse(payload);
  } catch (e) {
    return null;
  }
}

export function getAccessTokenPayload(token = localStorage.getItem("accessToken")) {
  return parseJwt(token);
}

export function getCompanyIdFromToken(token = localStorage.getItem("accessToken")) {
  const payload = getAccessTokenPayload(token);
  const companyId = payload && payload.companyId;
  if (companyId == null) return null;
  const parsed = Number(companyId);
  return Number.isNaN(parsed) ? null : parsed;
}

export function isTokenExpired(token, bufferSeconds = 0) {
  const payload = parseJwt(token);
  if (!payload || typeof payload.exp !== "number") {
    return true;
  }
  const now = Math.floor(Date.now() / 1000);
  return payload.exp <= now + bufferSeconds;
}

export function refreshToken() {
  if (!refreshTokenRequest) {
    const storedRefreshToken = localStorage.getItem("refreshToken");
    if (!storedRefreshToken) {
      return Promise.reject(new Error("No refresh token available"));
    }

    refreshTokenRequest = axios
      .post(`${process.env.VUE_APP_BASE_URL}/auth/refresh`, null, {
        headers: applyClientHeaders({
          Authorization: `Bearer ${storedRefreshToken}`,
        }),
      })
      .then((res) => {
        const { accessToken, refreshToken: newRefresh } = res.data;
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", newRefresh);
        axios.defaults.headers.common["Authorization"] =
          "Bearer " + accessToken;
        return accessToken;
      })
      .catch((err) => {
        console.error("Failed to refresh token:", err);
        throw err;
      })
      .finally(() => {
        refreshTokenRequest = null;
      });
  }

  return refreshTokenRequest;
}

export async function ensureValidAccessToken(bufferSeconds = 10) {
  const storedAccessToken = localStorage.getItem("accessToken");
  if (!storedAccessToken) {
    return null;
  }
  if (isTokenExpired(storedAccessToken, bufferSeconds)) {
    await refreshToken();
    return localStorage.getItem("accessToken");
  }
  return storedAccessToken;
}

axios.interceptors.request.use(
  async (config) => {
    if (config?.url && config.url.includes("/auth/refresh")) {
      return config;
    }
    try {
      const token = await ensureValidAccessToken();
      if (token) {
        config.headers = {
          ...applyClientHeaders(config.headers),
          Authorization: `Bearer ${token}`,
        };
      }
      config.headers = applyClientHeaders(config.headers);
    } catch (e) {
      // If refreshing fails, clear tokens to force re-auth later
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/auth/refresh")
    ) {
      originalRequest._retry = true;
      try {
        await refreshToken();
        originalRequest.headers = {
          ...applyClientHeaders(originalRequest.headers),
          Authorization: "Bearer " + localStorage.getItem("accessToken"),
        };
        originalRequest.headers = applyClientHeaders(originalRequest.headers);
        return axios(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);
